import {
  keepPreviousData,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import useApi from "../../api/apiConfig";
import {
  batchPostToPMS,
  type ClaimsSearchParams,
  createClaim,
  getClaimsProceduresAndPatient,
  getInsuranceArActionOptions,
  updateClaim,
  updateDenial,
  updateInsuranceArEntry,
  updateStringifyClaim,
} from "./api";

export const CLAIMS_KEY = ["claims"];
const LIST_KEY = [...CLAIMS_KEY, "list"];
const LATER_KEY = [...CLAIMS_KEY, "later"];

type ClaimsListKind = "list" | "later";
type ClaimsTransform = (response: SearchResponse) => SearchResponse;

const keyFor = (kind: ClaimsListKind) =>
  kind === "list" ? LIST_KEY : LATER_KEY;

function useClaimsQuery(
  kind: ClaimsListKind,
  params: ClaimsSearchParams,
  enabled: boolean,
  transform?: ClaimsTransform,
) {
  const { getClient } = useApi();
  return useQuery({
    queryKey: [...keyFor(kind), params],
    queryFn: async ({ signal }) => {
      const response = await getClaimsProceduresAndPatient(
        await getClient(),
        params,
        signal,
      );
      return transform ? transform(response) : response;
    },
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
  });
}

export const useClaims = (
  params: ClaimsSearchParams,
  enabled: boolean,
  transform?: ClaimsTransform,
) => useClaimsQuery("list", params, enabled, transform);

export const useLaterClaims = (
  params: ClaimsSearchParams,
  enabled: boolean,
  transform?: ClaimsTransform,
) => useClaimsQuery("later", params, enabled, transform);

export function useInsuranceArActionOptions(enabled: boolean) {
  const { getClient } = useApi();
  return useQuery({
    queryKey: ["insurance-ar", "action-options"],
    queryFn: async ({ signal }) =>
      getInsuranceArActionOptions(await getClient(), signal),
    enabled,
    retry: false,
  });
}

function updateClaimRows(
  queryClient: QueryClient,
  claimKey: string,
  update: (
    claim: ClaimWithProcedureAndPatientMessage,
  ) => ClaimWithProcedureAndPatientMessage,
) {
  queryClient.setQueriesData<SearchResponse>(
    { queryKey: CLAIMS_KEY },
    (old) => {
      if (!old?.data.some((c) => c.wieldyId === claimKey)) return old;
      return {
        ...old,
        data: old.data.map((c) => (c.wieldyId === claimKey ? update(c) : c)),
      };
    },
  );
}

export function patchClaim(
  queryClient: QueryClient,
  claim: ClaimWithProcedureAndPatientMessage,
) {
  updateClaimRows(queryClient, claim.wieldyId, () => claim);
}

export function removeClaimRow(
  queryClient: QueryClient,
  kind: ClaimsListKind,
  claimKey: string,
) {
  queryClient.setQueriesData<SearchResponse>(
    { queryKey: keyFor(kind) },
    (old) =>
      old?.data.some((c) => c.wieldyId === claimKey)
        ? { ...old, data: old.data.filter((c) => c.wieldyId !== claimKey) }
        : old,
  );
}

// Inserts and count bumps target the query on screen only. A prefix match would
// also write the row into cached lists for other filters and pages.
export function prependClaimRow(
  queryClient: QueryClient,
  kind: ClaimsListKind,
  params: ClaimsSearchParams,
  claim: ClaimWithProcedureAndPatientMessage,
  limit: number,
) {
  queryClient.setQueryData<SearchResponse>([...keyFor(kind), params], (old) =>
    old ? { ...old, data: [claim, ...old.data].slice(0, limit) } : old,
  );
}

export function appendClaimRow(
  queryClient: QueryClient,
  params: ClaimsSearchParams,
  claim: ClaimWithProcedureAndPatientMessage,
) {
  queryClient.setQueryData<SearchResponse>([...LIST_KEY, params], (old) =>
    old ? { ...old, data: [...old.data, claim] } : old,
  );
}

export function bumpClaimsTotal(
  queryClient: QueryClient,
  kind: ClaimsListKind,
  params: ClaimsSearchParams,
  delta: number,
) {
  queryClient.setQueryData<SearchResponse>([...keyFor(kind), params], (old) => {
    if (!old) return old;
    const total = Math.max(0, old.total + delta);
    return {
      ...old,
      total,
      total_pages: Math.max(1, Math.ceil(total / old.page_size)),
    };
  });
}

// Denial and claim writes are eventually consistent, so callers patch the
// cache and only invalidate where a reload is intended.
export function useUpdateClaim() {
  const { getClient } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ claimWork }: UpdateClaimVars) =>
      updateClaim(await getClient(), claimWork),
    onMutate: ({ claim, claimWork }) =>
      patchClaim(queryClient, { ...claim, ...claimWork }),
  });
}

interface UpdateClaimVars {
  claim: ClaimWithProcedureAndPatientMessage;
  claimWork: ClaimWork;
}

interface UpdateDenialVars {
  claimWork: ClaimWork;
  denialWork?: DenialWork;
  optimisticClaim?: ClaimWithProcedureAndPatientMessage;
}

export function useUpdateDenial() {
  const { getClient } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ claimWork, denialWork }: UpdateDenialVars) =>
      updateDenial(await getClient(), claimWork, denialWork),
    onMutate: ({ optimisticClaim }) => {
      if (optimisticClaim) patchClaim(queryClient, optimisticClaim);
    },
  });
}

export function useCreateClaim() {
  const { getClient } = useApi();
  return useMutation({
    mutationFn: async (claim: ClaimMessage) =>
      createClaim(await getClient(), claim),
  });
}

interface UpdateStringifyClaimVars {
  update: Partial<ClaimMessage>;
  patch: ClaimWithProcedureAndPatientMessage;
}

export function useUpdateStringifyClaim() {
  const { getClient } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ update }: UpdateStringifyClaimVars) =>
      updateStringifyClaim(await getClient(), update),
    onSuccess: (_data, { patch }) => patchClaim(queryClient, patch),
  });
}

export function useBatchPostToPMS() {
  const { getClient } = useApi();
  return useMutation({
    mutationFn: async () => batchPostToPMS(await getClient()),
  });
}

export function useUpdateInsuranceArEntry() {
  const { getClient } = useApi();
  return useMutation({
    mutationFn: async (payload: InsuranceArEntryUpdatePayload) =>
      updateInsuranceArEntry(await getClient(), payload),
  });
}

export type ClaimsCacheAction =
  | { type: "UPDATE_CLAIM"; claimToUpdate: ClaimWithProcedureAndPatientMessage }
  | { type: "DELETE_CLAIM"; claimKey: string }
  | {
      type: "CREATE_PROCEDURE" | "UPDATE_PROCEDURE";
      claimKey: string;
      procedure: ProcedureMessage;
    }
  | { type: "DELETE_PROCEDURE"; claimKey: string; procedureKey: string };

// Lets the shared crud/posting/validation helpers keep their dispatch-style callback.
export function applyClaimsAction(
  queryClient: QueryClient,
  action: ClaimsCacheAction,
) {
  switch (action.type) {
    case "UPDATE_CLAIM":
      patchClaim(queryClient, action.claimToUpdate);
      break;
    case "DELETE_CLAIM":
      removeClaimRow(queryClient, "list", action.claimKey);
      break;
    case "UPDATE_PROCEDURE":
      updateClaimRows(queryClient, action.claimKey, (claim) => ({
        ...claim,
        Procedure: claim.Procedure.map((proc) =>
          proc.wieldyId === action.procedure.wieldyId ? action.procedure : proc,
        ),
      }));
      break;
    case "CREATE_PROCEDURE":
      updateClaimRows(queryClient, action.claimKey, (claim) => ({
        ...claim,
        Procedure: [...claim.Procedure, action.procedure],
      }));
      break;
    case "DELETE_PROCEDURE":
      updateClaimRows(queryClient, action.claimKey, (claim) => ({
        ...claim,
        Procedure: claim.Procedure.filter(
          (procedure) => procedure.wieldyId !== action.procedureKey,
        ),
      }));
      break;
    default:
      break;
  }
}
